import { lv95ToWgs84ForOneCoordinate } from 'views/add-grid/map/coordinateTransforms';

export type CoordinateSystemMode = 'lv95' | 'custom';

export type CoordinateSystemPreference = {
  mode: CoordinateSystemMode;
  centerX: number;
  centerY: number;
  centerZ: number;
  alpha: number;
};

type CoordinateReferenceSystemPayload = {
  rotation?: number | string | null;
  point?: {
    x?: number | string | null;
    y?: number | string | null;
    z?: number | string | null;
  } | null;
};

type CoordinateSystemParams = {
  coordinateReferenceSystem?: CoordinateReferenceSystemPayload | null;
};

type XYCoordinate = {
  x: number;
  y: number;
};

type ProjectCoordinates = {
  x?: number | string | null;
  y?: number | string | null;
  lat?: number | null;
  lng?: number | null;
  [key: string]: unknown;
};

type ProjectWithCoordinates = {
  coordinates?: ProjectCoordinates | null;
  [key: string]: unknown;
};

export const defaultCoordinateSystemPreference: CoordinateSystemPreference = {
  mode: 'lv95',
  centerX: 0,
  centerY: 0,
  centerZ: 0,
  alpha: 0,
};

const roundCoordinateValue = (value: number): number => Math.round(value * 100) / 100;

const toNumber = (value: number | string | null | undefined): number => {
  const parsedValue = parseFloat(String(value ?? 0));
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

// JS/TS standard Math has no native toDegrees/toRadians helpers.
export const toDegrees = (radians: number | string | null | undefined): number => (toNumber(radians) * 180) / Math.PI;
export const toRadians = (degrees: number | string | null | undefined): number => (toNumber(degrees) * Math.PI) / 180;

export const getCoordinateSystemMode = ({
  centerX = 0,
  centerY = 0,
  centerZ = 0,
  alpha = 0,
}: Partial<CoordinateSystemPreference> = {}): CoordinateSystemMode =>
  centerX !== 0 || centerY !== 0 || centerZ !== 0 || alpha !== 0 ? 'custom' : 'lv95';

export const parseCoordinateSystemPreference = (params: CoordinateSystemParams = {}): CoordinateSystemPreference => {
  const coordinateReferenceSystem = params.coordinateReferenceSystem;
  const point = coordinateReferenceSystem?.point ?? {};
  const preference = {
    centerX: toNumber(point?.x),
    centerY: toNumber(point?.y),
    centerZ: toNumber(point?.z),
    alpha: roundCoordinateValue(toDegrees(coordinateReferenceSystem?.rotation)),
  };

  return {
    ...preference,
    mode: getCoordinateSystemMode(preference),
  };
};

export const buildCoordinateReferenceSystemPayload = (
  preference: Partial<CoordinateSystemPreference> = defaultCoordinateSystemPreference
) => {
  const nextPreference = {
    ...defaultCoordinateSystemPreference,
    ...preference,
  };

  if (getCoordinateSystemMode(nextPreference) === 'lv95') {
    return {
      coordinateReferenceSystem: {
        rotation: 0,
        point: {
          x: 0,
          y: 0,
          z: 0,
        },
      },
    };
  }

  return {
    coordinateReferenceSystem: {
      rotation: toRadians(nextPreference.alpha),
      point: {
        x: toNumber(nextPreference.centerX),
        y: toNumber(nextPreference.centerY),
        z: toNumber(nextPreference.centerZ),
      },
    },
  };
};

export const convertFromLv95ToUserCoordinate = (
  coordinate: XYCoordinate,
  coordinateSystem?: Partial<CoordinateSystemPreference>
): XYCoordinate => {
  if (coordinateSystem?.mode !== 'custom') {
    return coordinate;
  }

  const radians = toRadians(coordinateSystem.alpha);
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const dx = coordinate.x - toNumber(coordinateSystem.centerX);
  const dy = coordinate.y - toNumber(coordinateSystem.centerY);

  return {
    x: roundCoordinateValue(dx * cos + dy * sin),
    y: roundCoordinateValue(-dx * sin + dy * cos),
  };
};

export const convertFromUserToLv95Coordinate = (
  coordinate: XYCoordinate,
  coordinateSystem?: Partial<CoordinateSystemPreference>
): XYCoordinate => {
  if (coordinateSystem?.mode !== 'custom') {
    return coordinate;
  }

  const radians = toRadians(coordinateSystem.alpha);
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const x = toNumber(coordinateSystem.centerX) + coordinate.x * cos - coordinate.y * sin;
  const y = toNumber(coordinateSystem.centerY) + coordinate.x * sin + coordinate.y * cos;

  return {
    x: roundCoordinateValue(x),
    y: roundCoordinateValue(y),
  };
};

export const normalizeProjectCoordinatesForMap = <T extends ProjectWithCoordinates>(
  project: T | null | undefined
): T | null | undefined => {
  if (!project) return project;

  const coordinates = project.coordinates;
  if (!coordinates) {
    return project;
  }

  if (coordinates.lat != null && coordinates.lng != null) {
    return project;
  }

  if (coordinates.x != null && coordinates.y != null) {
    const { lat, lng } = lv95ToWgs84ForOneCoordinate(toNumber(coordinates.x), toNumber(coordinates.y));
    return {
      ...project,
      coordinates: {
        ...coordinates,
        lat,
        lng,
      },
    };
  }

  return project;
};
